import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Send as SendIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import merchantService from '../services/merchant.service';
import LoadingAnimation from '../components/LoadingAnimation';
import PaginationControls from '../components/PaginationControls';

export default function MerchantNotificationHistory() {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    fetchHistory();
  }, [page, rowsPerPage]);

  const fetchHistory = async () => {
    try {
      const data = await merchantService.getNotificationHistory({ page, limit: rowsPerPage });
      setHistory(data.history || []);
      setTotal(data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notification history:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingAnimation message="Loading notification history..." subtitle="Fetching sent notifications" />;
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Notification History
        </Typography>
        <Typography color="text.secondary">
          View all notifications you've sent to customers
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <SendIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          </Box>
          <Typography color="text.secondary" variant="body2">Total Sent</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {total}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <PeopleIcon sx={{ color: 'success.main', fontSize: 32 }} />
          </Box>
          <Typography color="text.secondary" variant="body2">Unique Recipients</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
            {history.reduce((acc, h) => acc + h.recipients.length, 0)}
          </Typography>
        </Paper>
      </Box>

      {/* History Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Message</strong></TableCell>
                <TableCell><strong>Recipients</strong></TableCell>
                <TableCell><strong>Read Rate</strong></TableCell>
                <TableCell><strong>Sent At</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography>Loading history...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No notifications sent yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && history.map((item, index) => {
                const readCount = item.recipients.filter((r: any) => r.read).length;
                const readRate = ((readCount / item.recipients.length) * 100).toFixed(0);

                return (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.message.length > 60
                          ? `${item.message.substring(0, 60)}...`
                          : item.message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography>{item.recipients.length}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${readRate}%`}
                          size="small"
                          color={parseInt(readRate) > 50 ? 'success' : 'default'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ({readCount}/{item.recipients.length})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {new Date(item.sentAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />
    </Box>
  );
}
